import { createContext, useContext, useEffect } from 'react';

// Split contexts so pages that only register context never re-render on
// assistant state changes (query keystrokes, loading, results):
// - registration context: stable callbacks/refs, consumed by pages
// - state context: live assistant state, consumed by the bottom sheet
export const MobileAiAssistantRegistrationContext = createContext({
    registerPageContext: () => {},
    pageHandlersRef: { current: {} },
    desktopInputRef: { current: null },
});

export const MobileAiAssistantStateContext = createContext(null);

export const useMobileAiAssistantState = () =>
    useContext(MobileAiAssistantStateContext);

// Opts the current page into the mobile AI assistant. While registered, the
// shared header shows the AI trigger and the bottom sheet uses this context.
//
// `context` must be concise serializable metadata (pageType, discoveryType,
// suggestions, ...). It is compared by value, so callers don't need to memoize.
// `handlers` (e.g. onApplyStructuredFilters) are kept in a ref, so unstable
// function identities are fine. Both are cleared on unmount.
export const useMobileAiPageContext = (context, handlers = {}) => {
    const { registerPageContext, pageHandlersRef } = useContext(
        MobileAiAssistantRegistrationContext
    );

    const serializedContext = JSON.stringify(context ?? null);

    useEffect(() => {
        registerPageContext(JSON.parse(serializedContext));
        return () => registerPageContext(null);
    }, [serializedContext, registerPageContext]);

    useEffect(() => {
        pageHandlersRef.current = handlers || {};
    });

    useEffect(() => () => {
        pageHandlersRef.current = {};
    }, [pageHandlersRef]);
};

// Desktop only: the inline Ask Abroadify card's input, so the floating
// launcher can scroll to and focus whichever card the page rendered.
export const useDesktopAiInputRef = () =>
    useContext(MobileAiAssistantRegistrationContext).desktopInputRef;
